import { Model } from 'app/model/backendmodel';
import { CopySource } from 'app/model/pipeline/copysource/copysource';
import { IModel } from 'app/model/base/annotation';
import { CopyActivity } from 'app/model/pipeline/copy.activity';
import { Owned } from 'app/model/base/named';
import { TabularCopySource } from '../../../pipeline/copysource/tabular-type.copysource';
import { ValueAdapter } from 'app/model/adapters/value.adapter';
import { columnValueToResource, columnResourceToValue } from '../../column.adapter.util';
import { NamedArray } from 'app/model/base/namedarray';
import { Column } from 'app/model/dataset/column';
import { IStructure } from '../../datasets/dataset.constant.adapter';
import { arrayIsNullOrEmpty } from 'app/registries/utls/util';
import { isExpressionObject, isLogicAppExpression } from 'app/shared/utils/expression.util';
import { AdditionalColumn, BaseAsyncAdapter, ITridentResource } from 'shell-models-lib';

export const commonCopySourceAdapts = {
    type: '*',
    additionalColumns: '*'
};

export abstract class CopySourceAdapter<T, U> extends BaseAsyncAdapter<T, U> {
    protected getCommonCopySourceProperties(_copySource: CopySource, copySourceType: string): Model.CopySource {
        const result: Model.CopySource = {
            type: copySourceType
        };
        if (CopySource.ADDITIONAL_COLUMNS.templateValue(_copySource)) {
            result.additionalColumns = {
                type: 'Expression',
                value: CopySource.ADDITIONAL_COLUMNS.templateValue(_copySource)
            };
        } else if (!arrayIsNullOrEmpty(_copySource.additionalColumns)) {
            result.additionalColumns = [];
            _copySource.additionalColumns.forEach((column) => {
                // handled undefined column.dupColumn
                const value =
                    !AdditionalColumn.VALUE.templateValue(column) && column.value === '$$COLUMN'
                        ? `${column.value}:${column.dupColumn || ''}`
                        : ValueAdapter.toResource(column, AdditionalColumn.VALUE) || '';
                result.additionalColumns.push({
                    name: column.name,
                    value
                });
            });
        }
        return result;
    }

    protected createCopySourceWithCommonProperties(
        model: IModel,
        _resourceCopySource: Model.CopySource,
        copyActivity: CopyActivity
    ): CopySource {
        const result = Owned.create<CopySource>(model, copyActivity);
        if (
            isExpressionObject(_resourceCopySource.additionalColumns) ||
            isLogicAppExpression(_resourceCopySource.additionalColumns)
        ) {
            ValueAdapter.toModel(result, CopySource.ADDITIONAL_COLUMNS, _resourceCopySource.additionalColumns);
        } else if (!arrayIsNullOrEmpty(_resourceCopySource.additionalColumns)) {
            result.additionalColumns = [];
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            _resourceCopySource.additionalColumns.forEach((column: any) => {
                const modelColumn = Owned.create<AdditionalColumn>(AdditionalColumn.MODEL, copyActivity);
                modelColumn.name = column.name;
                // handle undefined column.value
                if (
                    column.value != null &&
                    !isExpressionObject(column.value) &&
                    !isLogicAppExpression(column.value) &&
                    column.value.startsWith('$$COLUMN:')
                ) {
                    modelColumn.value = '$$COLUMN';
                    modelColumn.dupColumn = column.value.substring(9);
                } else {
                    ValueAdapter.toModel(modelColumn, AdditionalColumn.VALUE, column.value);
                }
                result.additionalColumns.push(modelColumn);
            });
        }
        return result;
    }

    protected _toTabularResource(
        copySource: TabularCopySource,
        copySourceType: string,
        queryAlias?: string
    ): Model.CopySource {
        const result: Model.CopySource = this.getCommonCopySourceProperties(copySource, copySourceType);
        const queryKeyword = queryAlias || 'query';
        if (copySource.mode === 'query') {
            result[queryKeyword] = ValueAdapter.toResource(copySource, TabularCopySource.READER_QUERY);
        }
        return result;
    }

    protected _toTabularModel(
        resource: Model.CopySource,
        copyActivity: CopyActivity,
        copySourceModel: IModel,
        queryAlias?: string
    ): TabularCopySource {
        const result = this.createCopySourceWithCommonProperties(
            copySourceModel,
            resource,
            copyActivity
        ) as TabularCopySource;
        const queryKeyword = queryAlias || 'query';
        if (resource[queryKeyword]) {
            ValueAdapter.toModel(result, TabularCopySource.READER_QUERY, resource[queryKeyword]);
            result.mode = 'query';
        }
        return result;
    }

    protected columnValueToResource(copySource: CopySource, columns: NamedArray<Column>): IStructure[] | undefined {
        return columnValueToResource(copySource, columns, this.context);
    }

    protected columnResourceToValue(copySource: CopySource, columns: IStructure[] | undefined): NamedArray<Column> {
        return columnResourceToValue(copySource, columns);
    }

    protected async _toAzureResource(tridentResource: ITridentResource): Promise<U> {
        throw `Artifact type '${tridentResource.artifactType}' does not implement _toAzureResource method.`;
    }
}
